import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api, btnDanger, btnPrimary, btnSecondary, InlineError, input, labelCls, Modal, useToast } from '../ui/Kit';
import { AlexScheduleDialog } from '../candidates/AlexDialogs';
import { RoundScheduleDialog } from '../candidates/InterviewDialogs';
import CompareDialog from './CompareDialog';

// Job dialogs (bundle 3a-2): every one says what will happen, shows errors inline and toasts on success.
// props: job (a list row / the job page's job), onClose(), onDone() — the parent reloads.

function useAction(onDone, success) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true); setError('');
    try { const out = await fn(); toast.show(typeof success === 'function' ? success(out) : success); onDone(out); return out; }
    catch (e) { setError(e.message); setBusy(false); return null; }
  };
  return { busy, error, setError, run };
}

const title = (job) => `${job.req_id ? `${job.req_id} · ` : ''}${job.job_title}`;

export function HoldDialog({ job, onClose, onDone }) {
  const [note, setNote] = useState('');
  const a = useAction(onDone, 'Job put on hold');
  return (
    <Modal open title={`Put on hold — ${job.job_title}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={a.busy} onClick={() => a.run(() => api(`/api/jobs/${job.id}/hold`, { method: 'POST', body: { note } }))}>Put on hold</button></>}>
      <p className="text-sm text-[#334155]">Recruiting pauses and the job's ageing stops until it's taken off hold. The recruiters and the hiring manager are told.</p>
      <label className="block mt-3"><span className={labelCls}>Why (optional)</span>
        <textarea className={input} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></label>
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

export function ConfirmAction({ job, kind, onClose, onDone }) {
  const conf = {
    resume: ['Take off hold', `Recruiting resumes on ${title(job)} and its ageing starts again.`, 'Take off hold', 'POST', 'resume', 'Job is open again', false],
    reopen: ['Reopen job', `${title(job)} becomes open again.`, 'Reopen', 'POST', 'reopen', 'Job reopened', false],
    delete: ['Delete job', `${title(job)} is removed from every list. Anything waiting for approval is cancelled. This is recorded on the job's timeline.`, 'Delete', 'DELETE', '', 'Job deleted', true],
  }[kind];
  const a = useAction(onDone, conf[5]);
  return (
    <Modal open title={conf[0]} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={conf[6] ? btnDanger : btnPrimary} disabled={a.busy}
          onClick={() => a.run(() => api(`/api/jobs/${job.id}${conf[4] ? `/${conf[4]}` : ''}`, { method: conf[3] }))}>{a.busy ? 'Working…' : conf[2]}</button></>}>
      <p className="text-sm text-[#334155]">{conf[1]}</p>
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

export function CloseDialog({ job, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [ivs, setIvs] = useState(null);
  const [ok, setOk] = useState(false);
  const a = useAction(onDone, 'Job closed');
  useEffect(() => { api(`/api/jobs/${job.id}/close-check`).then((r) => setIvs(r.scheduled_interviews || [])).catch(() => setIvs([])); }, [job.id]);
  const needConfirm = ivs && ivs.length > 0;
  return (
    <Modal open title={`Close — ${job.job_title}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={a.busy || !reason || ivs === null || (needConfirm && !ok)}
          onClick={() => a.run(() => api(`/api/jobs/${job.id}/close`, { method: 'POST', body: { reason, note, confirm_interviews: ok } }))}>Close job</button></>}>
      <fieldset><legend className={labelCls}>Why is it closing?</legend>
        <div className="flex gap-4 text-sm">
          {[['filled', 'Filled'], ['cancelled', 'Cancelled']].map(([v, l]) => (
            <label key={v} className="flex items-center gap-2"><input type="radio" name="close-reason" value={v} checked={reason === v} onChange={() => setReason(v)} />{l}</label>
          ))}
        </div></fieldset>
      <label className="block mt-3"><span className={labelCls}>Note (optional)</span>
        <textarea className={input} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></label>
      {needConfirm && (
        <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm" data-testid="close-interviews">
          <p className="font-semibold text-amber-900">{ivs.length} Quali5d AI interview{ivs.length > 1 ? 's are' : ' is'} still scheduled</p>
          <ul className="mt-1 text-amber-900">{ivs.map((i) => <li key={i.interview_id}>{i.candidate_name}{i.scheduled_at ? ` · ${new Date(`${i.scheduled_at}Z`).toLocaleString()}` : ''}</li>)}</ul>
          <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />
            Close anyway — these interviews stay scheduled (cancel them on the Interviews page if needed)</label>
        </div>
      )}
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

export function ChangeDialog({ job, onClose, onDone }) {
  const [t, setT] = useState(job.job_title || '');
  const [pay, setPay] = useState({ currency: 'USD', base_min: '', base_max: '', bonus: '' });
  const [usePay, setUsePay] = useState(false);
  const a = useAction(onDone, (r) => (r && r.approval_needed ? 'Change sent for approval' : 'Job updated'));
  const body = () => {
    const b = {};
    if (t.trim() && t.trim() !== job.job_title) b.job_title = t.trim();
    if (usePay) b.compensation = { currency: pay.currency, base_min: Number(pay.base_min), base_max: Number(pay.base_max), ...(pay.bonus ? { bonus: pay.bonus } : {}) };
    return b;
  };
  return (
    <Modal open title={`Change title or pay — ${job.job_title}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={a.busy || Object.keys(body()).length === 0}
          onClick={() => a.run(() => api(`/api/jobs/${job.id}/change`, { method: 'POST', body: body() }))}>Send for approval</button></>}>
      <p className="text-sm text-[#334155]">The change goes back through approval. Until it's approved the job stays live with its current title and pay.</p>
      <label className="block mt-3"><span className={labelCls}>Job title</span><input className={input} value={t} onChange={(e) => setT(e.target.value)} maxLength={255} /></label>
      <label className="flex items-center gap-2 mt-3 text-sm font-semibold"><input type="checkbox" checked={usePay} onChange={(e) => setUsePay(e.target.checked)} />Change the pay range</label>
      {usePay && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          <label className="block"><span className={labelCls}>Currency</span>
            <select className={input} value={pay.currency} onChange={(e) => setPay({ ...pay, currency: e.target.value })}>
              {['USD', 'CAD', 'EUR', 'GBP', 'INR', 'AUD'].map((c) => <option key={c}>{c}</option>)}</select></label>
          <label className="block"><span className={labelCls}>Minimum</span><input className={input} inputMode="numeric" value={pay.base_min} onChange={(e) => setPay({ ...pay, base_min: e.target.value })} /></label>
          <label className="block"><span className={labelCls}>Maximum</span><input className={input} inputMode="numeric" value={pay.base_max} onChange={(e) => setPay({ ...pay, base_max: e.target.value })} /></label>
          <label className="block"><span className={labelCls}>Bonus (optional)</span><input className={input} value={pay.bonus} onChange={(e) => setPay({ ...pay, bonus: e.target.value })} /></label>
        </div>
      )}
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

// Assign / Reassign / Recruit myself / Make primary / Remove — one dialog over GET /assignable
export function AssignDialog({ job, mode = 'assign', onClose, onDone }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [note, setNote] = useState('');
  const [q, setQ] = useState('');
  const a = useAction(onDone, (r) => (r && r.team && r.team.length ? `Assigned · ${r.team.length} recruiter${r.team.length > 1 ? 's' : ''}` : 'Recruiters removed'));
  useEffect(() => {
    api(`/api/jobs/${job.id}/assignable`).then((d) => {
      setData(d);
      const team = d.team || [];
      let ids = team.map((t) => t.user_id);
      let prim = (team.find((t) => t.is_primary) || {}).user_id || null;
      if (mode === 'myself') {
        const me = (d.people || []).find((p) => p.you);
        if (me && !ids.includes(me.user_id)) ids = [me.user_id, ...ids];
        if (me && !prim) prim = me.user_id;
      }
      setPicked(ids); setPrimary(prim);
    }).catch((e) => { a.setError(e.message); setData({ people: [], leads: [], team: [] }); });
  }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => {
    if (picked.includes(id)) { const rest = picked.filter((x) => x !== id); setPicked(rest); if (primary === id) setPrimary(rest[0] || null); }
    else { setPicked([...picked, id]); if (!primary) setPrimary(id); }
  };
  const people = ((data && data.people) || []).filter((p) => !q.trim() || p.full_name.toLowerCase().includes(q.trim().toLowerCase()));
  const heading = { assign: 'Assign', reassign: 'Reassign', myself: 'Recruit myself' }[mode];
  return (
    <Modal open title={`${heading} — ${title(job)}${job.openings ? ` · ${job.openings} opening${job.openings > 1 ? 's' : ''}` : ''}`} onClose={onClose} width="max-w-xl"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={a.busy || !data || (picked.length > 0 && !primary)}
          onClick={() => a.run(() => api(`/api/jobs/${job.id}/assignments`, { method: 'PUT', body: { user_ids: picked, primary_id: primary, note } }))}>
          {picked.length ? `Assign ${picked.length} ${picked.length > 1 ? 'people' : 'person'}` : 'Remove everyone'}</button></>}>
      <p className="text-sm text-[#334155]">You can assign anyone who reports to you — and yourself. Pick one or more; mark one as primary.</p>
      <input aria-label="Search your team" className={input + ' mt-3'} placeholder="Search your team" value={q} onChange={(e) => setQ(e.target.value)} />
      {!data ? <p className="text-sm text-[#64748B] mt-3">Loading your team…</p> : (
        <ul className="mt-3 divide-y divide-[#EEF1F6] border border-[#E6E9F0] rounded-lg max-h-[45vh] overflow-y-auto">
          {!people.length && <li className="px-3 py-4 text-sm text-[#64748B]">Nobody in your team can recruit this job. Set "Reports to" in Organization, or give people the Recruit permission.</li>}
          {people.map((p) => {
            const on = picked.includes(p.user_id);
            const pct = Math.min(100, Math.round((p.open_jobs / Math.max(1, p.capacity)) * 100));
            return (
              <li key={p.user_id} className={`px-3 py-2.5 flex items-center gap-3 ${p.eligible ? '' : 'opacity-50'}`}>
                <input type="checkbox" aria-label={`Assign ${p.full_name}`} checked={on} disabled={!p.eligible} onChange={() => toggle(p.user_id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{p.full_name}{p.you ? ' (you)' : ''}</p>
                  <p className="text-xs text-[#64748B]">{[p.designation, `${p.open_jobs} open job${p.open_jobs === 1 ? '' : 's'} / ${p.capacity}`, p.reason].filter(Boolean).join(' · ')}</p>
                  <div className="h-1.5 rounded-full bg-[#EEF1F6] mt-1 w-40"><div className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-[#DC2626]' : pct >= 80 ? 'bg-[#F59E0B]' : 'bg-[#0066CC]'}`} style={{ width: `${pct}%` }} /></div>
                </div>
                {on && (primary === p.user_id
                  ? <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#E8F1FB] text-[#003D82]">PRIMARY</span>
                  : <button type="button" className="text-xs font-semibold text-[#0066CC] hover:underline" onClick={() => setPrimary(p.user_id)}>Make primary</button>)}
              </li>
            );
          })}
        </ul>
      )}
      <label className="block mt-3"><span className={labelCls}>Note to the team (optional)</span>
        <textarea className={input} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></label>
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

export function HandOffDialog({ job, onClose, onDone }) {
  const [leads, setLeads] = useState(null);
  const [lead, setLead] = useState('');
  const [note, setNote] = useState('');
  const a = useAction(onDone, 'Handed to the lead');
  useEffect(() => { api(`/api/jobs/${job.id}/assignable`).then((d) => setLeads(d.leads || [])).catch((e) => { a.setError(e.message); setLeads([]); }); }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Modal open title={`Hand to lead — ${title(job)}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={a.busy || !lead} onClick={() => a.run(() => api(`/api/jobs/${job.id}/hand-off`, { method: 'POST', body: { lead_id: Number(lead), note } }))}>Hand off</button></>}>
      <p className="text-sm text-[#334155]">The lead gets the job in their "To assign" list and assigns it within their team.</p>
      <label className="block mt-3"><span className={labelCls}>Lead</span>
        <select className={input} value={lead} onChange={(e) => setLead(e.target.value)} disabled={!leads}>
          <option value="">{leads === null ? 'Loading…' : leads.length ? 'Choose a lead…' : 'No leads report to you'}</option>
          {(leads || []).map((l) => <option key={l.user_id} value={l.user_id}>{l.full_name}{l.team_size ? ` · team of ${l.team_size}` : ''}</option>)}
        </select></label>
      <label className="block mt-3"><span className={labelCls}>Note (optional)</span>
        <textarea className={input} rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} /></label>
      <div className="mt-3"><InlineError>{a.error}</InlineError></div>
    </Modal>
  );
}

// The candidates behind a funnel number (4c live-test fixes): tabs Pending · Shortlisted · Out, opening on the one
// clicked; each row: name → profile, status, days in stage, owner, reason if out, the one next action; paginated.
// The server applies job access, HM visibility and pay visibility.
const TAB_LABEL = { pending: 'Pending', shortlisted: 'Shortlisted', out: 'Out' };
export function FunnelDialog({ job, column, tab: tab0, part: part0, round, onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(tab0 || 'pending');
  const [part, setPart] = useState(part0 || null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [alexFor, setAlexFor] = useState(null);       // 4f: Schedule Alex screen opens the one-step dialog here
  const [roundFor, setRoundFor] = useState(null);     // 5a: Schedule round N
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setData(null);
    const q = new URLSearchParams({ tab, page: String(page) });
    if (part) q.set('part', part);
    if (round) q.set('round', String(round));
    api(`/api/jobs/${job.id}/funnel/${column}?${q.toString()}`).then(setData).catch((e) => setError(e.message));
  }, [job.id, column, tab, part, round, page, reload]);
  const act = (r) => {                                  // the server gives each person the actions their role allows
    const a = r.action;
    if (!a) return;
    if (a.key === 'schedule_alex') setAlexFor(r.application_id);
    else if (a.key === 'schedule_round') setRoundFor(r.application_id);
    else if (a.key === 'review') navigate(a.link || (a.interview_id ? `/phase3/interview/${a.interview_id}` : r.link));
    else if (r.link) navigate(r.link);                  // View and every other action: this viewer's link for the candidate
  };
  const pick = (t) => { setTab(t); setPart(null); setPage(1); };
  return createPortal(
    <Modal open title={`${data ? data.label : ''}${round ? ` · round ${round}` : ''} — ${job.job_title}`} onClose={onClose} width="max-w-2xl">
      <div className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl mb-3 w-max" role="tablist">
        {['pending', 'shortlisted', 'out'].map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => pick(t)} data-testid={`ftab-${t}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === t ? 'bg-white text-[#0F172A] shadow' : 'text-[#475569]'}`}>
            {TAB_LABEL[t]}{data ? ` · ${data.counts[t]}` : ''}</button>))}
      </div>
      {data && data.chips && data.chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" role="group" aria-label="Sub-status" data-testid="funnel-chips">
          {[{ key: null, label: 'All', count: data.all }, ...data.chips].map((c) => (
            <button key={c.key || 'all'} type="button" aria-pressed={part === c.key} onClick={() => { setPart(c.key); setPage(1); }} data-testid={`fchip-${c.key || 'all'}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${part === c.key ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>
              {c.label} {c.count}</button>))}
        </div>)}
      <InlineError>{error}</InlineError>
      {!data && !error && <p className="text-sm text-[#64748B]">Loading…</p>}
      {data && (!data.rows.length ? <p className="text-sm text-[#64748B]">No candidates here.</p> : (
        <ul className="divide-y divide-[#EEF1F6]" data-testid="funnel-rows">
          {data.rows.map((r) => (
            <li key={r.application_id ? `a${r.application_id}` : `i${r.interview_id}`} className="py-2.5 flex items-center gap-3 text-sm">
              <span className="flex-1 min-w-0">
                {r.link ? <button type="button" className="font-semibold text-[#0F172A] hover:text-[#0066CC] text-left" onClick={() => navigate(r.link)} data-testid="funnel-name">{r.name}</button>
                  : <span className="font-semibold text-[#0F172A]" data-testid="funnel-name-text">{r.name}</span>}
                <span className={`block text-xs ${r.overdue || r.action_needed ? 'text-[#B45309] font-semibold' : 'text-[#64748B]'}`} data-testid="funnel-row-status">{[r.status, r.response ? `candidate: ${r.response}${r.action_needed ? ' — action needed' : ''}` : null, r.round ? `round ${r.round}` : null, `${r.days} day${r.days === 1 ? '' : 's'} in stage`, r.owner ? `owner ${r.owner}` : null].filter(Boolean).join(' · ')}</span>
                {r.reason && <span className="block text-xs text-[#991B1B]">{r.reason}</span>}
              </span>
              {r.action && <button type="button" className={btnSecondary + ' text-xs py-1.5'} onClick={() => act(r)} data-testid="funnel-action">{r.action.label}</button>}
            </li>
          ))}
        </ul>
      ))}
      {data && data.total > data.page_size && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button type="button" className={btnSecondary} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="text-[#64748B]">{page} / {Math.ceil(data.total / data.page_size)}</span>
          <button type="button" className={btnSecondary} disabled={page * data.page_size >= data.total} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
      {alexFor && <AlexScheduleDialog appId={alexFor} onClose={() => setAlexFor(null)} onDone={() => { setAlexFor(null); setReload((n) => n + 1); }} />}
      {roundFor && <RoundScheduleDialog appId={roundFor} onClose={() => setRoundFor(null)} onDone={() => { setRoundFor(null); setReload((n) => n + 1); }} />}
    </Modal>, document.body);
}

// Which dialog to show for an action key
export function JobDialog({ dialog, onClose, onDone }) {
  if (!dialog) return null;
  const { kind, job } = dialog;
  if (kind === 'hold') return <HoldDialog job={job} onClose={onClose} onDone={onDone} />;
  if (kind === 'close') return <CloseDialog job={job} onClose={onClose} onDone={onDone} />;
  if (kind === 'change') return <ChangeDialog job={job} onClose={onClose} onDone={onDone} />;
  if (['resume', 'reopen', 'delete'].includes(kind)) return <ConfirmAction job={job} kind={kind} onClose={onClose} onDone={onDone} />;
  if (['assign', 'reassign', 'myself'].includes(kind)) return <AssignDialog job={job} mode={kind} onClose={onClose} onDone={onDone} />;
  if (kind === 'hand_off') return <HandOffDialog job={job} onClose={onClose} onDone={onDone} />;
  if (kind === 'compare') return <CompareDialog job={job} onClose={onClose} />;
  if (kind === 'stage') return <FunnelDialog job={job} column={dialog.column} tab={dialog.tab} part={dialog.part} round={dialog.round} onClose={onClose} />;
  return null;
}
